/**
 * @file useOrdersRegistry.ts
 * @description Данные реестра заказов (phase19.31, зона «Заказы», макет (а)).
 *
 * Состояние фильтров и страницы — в адресе (`lib/order-registry-query.ts`),
 * хук только читает его и ходит на сервер постранично (`offset/limit`).
 *
 * Перечитывание тихое: прежний список остаётся на экране, пока идёт новый
 * запрос (строки-заготовки — только при первой загрузке, это делает
 * `ResponsiveList`). Ошибка при уже показанном списке список не стирает —
 * над ним встаёт полоса «данные могут быть устаревшими» (разбор A §3).
 * Гонку ответов (быстро листали страницы) гасит номер запроса.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import { ApiError } from "../lib/api-client";
import { listOrders, type OrderDto } from "../lib/api-orders";
import {
  parseOrdersRegistryQuery,
  patchOrdersRegistryQuery,
  resetOrdersRegistryFilters,
  toListOrdersParams,
  writeOrdersRegistryQuery,
  type OrdersRegistryQuery,
} from "../lib/order-registry-query";

/** Тексты ошибок: без списка и со списком на экране (разбор A §3). */
export const ORDERS_ERROR_TEXT = {
  empty: "Не удалось загрузить заказы. Проверьте связь и попробуйте ещё раз",
  stale: "Не удалось обновить список, данные могут быть устаревшими",
} as const;

export interface OrdersRegistryState {
  query: OrdersRegistryQuery;
  setQuery: (patch: Partial<OrdersRegistryQuery>) => void;
  resetFilters: () => void;
  items: OrderDto[];
  total: number;
  loading: boolean;
  /** Текст ошибки для `ResponsiveList`; `null` — ошибки нет. */
  error: string | null;
  refresh: () => void;
}

export function useOrdersRegistry(): OrdersRegistryState {
  const [params, setParams] = useSearchParams();
  // Ключ — сам адрес: новый объект состояния не перезапускает запрос зря.
  const key = writeOrdersRegistryQuery(parseOrdersRegistryQuery(params)).toString();
  const query = parseOrdersRegistryQuery(new URLSearchParams(key));
  const [items, setItems] = useState<OrderDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const requestId = useRef(0);

  useEffect(() => {
    const id = ++requestId.current;
    setLoading(true);
    listOrders(toListOrdersParams(parseOrdersRegistryQuery(new URLSearchParams(key))))
      .then((res) => {
        if (id !== requestId.current) return;
        setItems(res.items);
        setTotal(res.total);
        setFailed(false);
      })
      .catch((err: unknown) => {
        if (id !== requestId.current) return;
        // Причину сервера в консоль, человеку — понятный текст.
        if (!(err instanceof ApiError) && import.meta.env.DEV) console.warn("listOrders", err);
        setFailed(true);
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
  }, [key, attempt]);

  const setQuery = useCallback(
    (patch: Partial<OrdersRegistryQuery>) => {
      setParams((prev) => writeOrdersRegistryQuery(patchOrdersRegistryQuery(parseOrdersRegistryQuery(prev), patch)), {
        replace: true,
      });
    },
    [setParams],
  );

  const resetFilters = useCallback(() => {
    setParams((prev) => writeOrdersRegistryQuery(resetOrdersRegistryFilters(parseOrdersRegistryQuery(prev))), {
      replace: true,
    });
  }, [setParams]);

  const refresh = useCallback(() => setAttempt((n) => n + 1), []);

  return {
    query,
    setQuery,
    resetFilters,
    items,
    total,
    loading,
    error: failed ? (items.length > 0 ? ORDERS_ERROR_TEXT.stale : ORDERS_ERROR_TEXT.empty) : null,
    refresh,
  };
}
